import {
  type AgentProvider,
  type ConfigResponse,
  SETUP_EXTRA_PREFIX
} from '@shared/all/domains/electron';

export type SetupPhase =
  | { phase: 'checking' }
  | { phase: 'idle' }
  | { phase: 'running'; status: string }
  | { phase: 'ready' }
  | { phase: 'error'; error: string };

// Snapshot of the last server-confirmed values; used to compute isDirty so
// the Save button is disabled when nothing has changed.
type Baseline = {
  repoUrl: string;
  mainBranch: string;
  gitName: string;
  gitEmail: string;
  hasGithubToken: boolean;
  provider: AgentProvider;
};

export type CardAIConfigState = {
  // Form fields
  repoUrl: string;
  mainBranch: string;
  gitName: string;
  gitEmail: string;
  // CLI agent this space runs
  provider: AgentProvider;
  // Persisted `claude /login` creds on the runner (the only Claude auth)
  claudeLoggedIn: boolean;
  // Persisted `codex login` creds on the runner
  codexLoggedIn: boolean;
  // GitHub token
  githubToken: string;
  hasGithubToken: boolean;
  githubTokenHint: string;
  // OneDrive is the index (always available alongside git), not a backend.
  onedriveConnected: boolean;
  // Whether the host encrypts connection tokens at rest, as reported.
  // undefined = the runner never reported it (too old to know the field), which
  // is NOT the same as reporting false. Neither state gets credited with
  // encryption, but only `false` licenses the UI to blame a missing keychain.
  tokensEncrypted: boolean | undefined;
  // Why the host cannot read or store connections right now (no keychain, or
  // files still locked behind the connection password). Empty when it can -
  // and when it is set, the empty fields above mean "unreadable", not "unset".
  storageProblem: string;
  // UI state
  loading: boolean;
  saving: boolean;
  saved: boolean;
  // Environment setup lifecycle
  setup: SetupPhase;
  // Last server-confirmed snapshot (for dirty detection)
  baseline: Baseline;
};

export type CardAIConfigAction =
  | {
      type: 'SET_FIELD';
      field: 'repoUrl' | 'mainBranch' | 'gitName' | 'gitEmail' | 'githubToken';
      value: string;
    }
  | { type: 'SET_PROVIDER'; provider: AgentProvider }
  | { type: 'LOADED_CONFIG'; config: ConfigResponse }
  | { type: 'SAVE_START' }
  | { type: 'SAVE_COMPLETE'; config: ConfigResponse }
  | { type: 'SAVE_FLASH_DONE' }
  | { type: 'CLEAR_TOKEN'; which: 'github' }
  | { type: 'SET_SETUP'; setup: SetupPhase }
  | { type: 'SETUP_PROGRESS'; status: string };

export const cardAIConfigInitialState: CardAIConfigState = {
  repoUrl: '',
  mainBranch: 'main',
  gitName: '',
  gitEmail: '',
  provider: 'claude',
  claudeLoggedIn: false,
  codexLoggedIn: false,
  githubToken: '',
  hasGithubToken: false,
  githubTokenHint: '',
  onedriveConnected: false,
  tokensEncrypted: undefined,
  storageProblem: '',
  loading: true,
  saving: false,
  saved: false,
  setup: { phase: 'checking' },
  baseline: {
    repoUrl: '',
    mainBranch: 'main',
    gitName: '',
    gitEmail: '',
    hasGithubToken: false,
    provider: 'claude'
  }
};

function storageProblemOf(config: ConfigResponse): string {
  const store = config.secureStore;
  return store && !store.ready ? store.problem : '';
}

export function cardAIConfigReducer(
  state: CardAIConfigState,
  action: CardAIConfigAction
): CardAIConfigState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_PROVIDER':
      return { ...state, provider: action.provider };
    case 'LOADED_CONFIG':
      return {
        ...state,
        repoUrl: action.config.repoUrl,
        mainBranch: action.config.mainBranch,
        gitName: action.config.gitName,
        gitEmail: action.config.gitEmail,
        // Keep an unsaved provider choice across a mid-edit refresh (signing in
        // refetches the config); only adopt the server value on the first load.
        // Otherwise completing Codex sign-in would snap the tab back to Claude.
        provider: state.loading
          ? (action.config.provider ?? 'claude')
          : state.provider,
        // Runners predating /login support omit this over the wire.
        claudeLoggedIn: action.config.claudeLoggedIn ?? false,
        codexLoggedIn: action.config.codexLoggedIn ?? false,
        hasGithubToken: action.config.hasGithubToken,
        githubTokenHint: action.config.githubTokenHint,
        // A runner whose version predates OneDrive support omits this field
        // over the wire — treat a missing block as "not connected".
        onedriveConnected: action.config.onedrive?.connected ?? false,
        tokensEncrypted: action.config.tokensEncrypted,
        storageProblem: storageProblemOf(action.config),
        loading: false,
        baseline: {
          repoUrl: action.config.repoUrl,
          mainBranch: action.config.mainBranch,
          gitName: action.config.gitName,
          gitEmail: action.config.gitEmail,
          hasGithubToken: action.config.hasGithubToken,
          provider: action.config.provider ?? 'claude'
        }
      };
    case 'SAVE_START':
      return { ...state, saving: true };
    case 'SAVE_COMPLETE':
      return {
        ...state,
        provider: action.config.provider ?? 'claude',
        claudeLoggedIn: action.config.claudeLoggedIn ?? false,
        codexLoggedIn: action.config.codexLoggedIn ?? false,
        hasGithubToken: action.config.hasGithubToken,
        githubTokenHint: action.config.githubTokenHint,
        githubToken: '',
        // A runner whose version predates OneDrive support omits this field
        // over the wire — treat a missing block as "not connected".
        onedriveConnected: action.config.onedrive?.connected ?? false,
        tokensEncrypted: action.config.tokensEncrypted,
        storageProblem: storageProblemOf(action.config),
        saving: false,
        saved: true,
        baseline: {
          repoUrl: action.config.repoUrl,
          mainBranch: action.config.mainBranch,
          gitName: action.config.gitName,
          gitEmail: action.config.gitEmail,
          hasGithubToken: action.config.hasGithubToken,
          provider: action.config.provider ?? 'claude'
        }
      };
    case 'SAVE_FLASH_DONE':
      return { ...state, saved: false };
    case 'CLEAR_TOKEN':
      // The token was cleared on the server, so update both current state and
      // baseline so isDirty stays false right after a clear.
      return {
        ...state,
        hasGithubToken: false,
        baseline: { ...state.baseline, hasGithubToken: false }
      };
    case 'SET_SETUP':
      return { ...state, setup: action.setup };
    case 'SETUP_PROGRESS':
      // Don't downgrade ready/error from a stale progress event - refresh
      // handles transitions back into running when needed.
      if (state.setup.phase === 'ready' || state.setup.phase === 'error') {
        return state;
      }
      // An extra's install, not Initialize (useEnvironmentInstall reads it).
      if (action.status.startsWith(SETUP_EXTRA_PREFIX)) return state;
      return {
        ...state,
        setup: { phase: 'running', status: action.status }
      };
    default:
      return state;
  }
}
